// 중계방은 Supabase Realtime 을 쓰지 않는다. 상태는 워커에서 한 번 받고, 이후 변경은 WebSocket 으로
// 온다. 주기적 폴링을 하지 않는다.
import { hasSupabaseConfig, supabase } from '../../../shared/api/supabaseClient.js';

const REALTIME_API_URL = 'https://realtime.doroparty.com';
const GUEST_TOKEN_KEY = 'doroparty_broadcast_guest';

const ERROR_MESSAGES = {
  missing_bearer_token: '로그인 세션을 확인할 수 없습니다. 다시 로그인해주세요.',
  invalid_access_token: '로그인 세션이 만료됐습니다. 다시 로그인해주세요.',
  platform_admin_required: '플랫폼 관리자만 할 수 있는 작업입니다.',
  broadcast_control_denied: '중계를 제어할 권한이 없습니다. 중계방 매니저로 지정돼야 합니다.',
  youtube_url_invalid: '유튜브 주소를 확인해주세요. 영상 주소만 등록할 수 있습니다.',
  video_id_required: '등록할 유튜브 주소를 입력해주세요.',
  user_id_required: '지정할 계정을 선택해주세요.',
  broadcast_unavailable: '중계방 서버에 연결하지 못했습니다. 잠시 후 다시 시도해주세요.',
  broadcast_not_live: '중계 중이 아닐 때는 영상만 교체할 수 없습니다. 중계를 시작해주세요.',
  playback_video_changed: '영상이 바뀌었습니다. 새 영상에서 다시 맞춰주세요.',
  playback_owner_changed: '다른 진행자가 동기화 기준을 설정했습니다.',
  playback_invalid: '재생 위치를 확인하지 못했습니다. 잠시 후 다시 시도해주세요.',
  entry_closed: '지금은 중계방 입장이 닫혀 있습니다.',
  broadcast_session_unavailable: '중계방 입장 세션을 발급하지 못했습니다.',
  playlist_full: '재생 목록은 100개까지 담을 수 있습니다.',
  playlist_changed: '그 사이 재생 목록이 바뀌었습니다. 다시 시도해주세요.',
  playlist_item_not_found: '이미 지워진 영상입니다.',
  playlist_items_required: '추가할 유튜브 주소를 입력해주세요.',
};

function broadcastError(error, fallback) {
  const raw = String(error?.message || error || '');
  const code = Object.keys(ERROR_MESSAGES).find((candidate) => raw.includes(candidate));
  const next = new Error(ERROR_MESSAGES[code] || fallback || raw || '중계방 요청을 처리하지 못했습니다.');
  next.code = code || 'unknown_error';
  return next;
}

async function accessToken() {
  if (!hasSupabaseConfig || !supabase) return '';
  try {
    const result = await supabase.auth.getSession();
    return String(result.data?.session?.access_token || '');
  } catch {
    return '';
  }
}

// 지금 로그인한 계정. 저장된 세션을 읽을 뿐이라 평소에는 통신하지 않는다(토큰이 이미 지났으면
// Supabase 가 스스로 갱신한다). 받아 둔 입장 토큰을 다시 써도 되는지 판단할 때 쓴다 - 다른 탭에서
// 로그아웃하거나 계정을 바꿨으면 여기서 값이 달라지므로 토큰을 버리게 된다.
export async function currentAuthUserId() {
  if (!hasSupabaseConfig || !supabase) return '';
  try {
    const result = await supabase.auth.getSession();
    return String(result.data?.session?.user?.id || '');
  } catch {
    return '';
  }
}

async function request(path, { method = 'GET', body = null, authorize = false, signal } = {}) {
  const headers = { accept: 'application/json' };
  if (body) headers['content-type'] = 'application/json';
  if (authorize) {
    const token = await accessToken();
    if (token) headers.authorization = `Bearer ${token}`;
  }
  const response = await fetch(`${REALTIME_API_URL}${path}`, {
    method,
    headers,
    signal,
    body: body ? JSON.stringify(body) : undefined,
  });
  let payload = null;
  try { payload = await response.json(); } catch { payload = null; }
  if (!response.ok) throw broadcastError(new Error(String(payload?.error || `broadcast_failed_${response.status}`)));
  return payload || {};
}

export function readStoredGuestToken() {
  try { return String(window.localStorage.getItem(GUEST_TOKEN_KEY) || ''); } catch { return ''; }
}

function storeGuestToken(token) {
  try { window.localStorage.setItem(GUEST_TOKEN_KEY, token); } catch { /* 사생활 보호 모드 */ }
}

// 세션 토큰은 워커가 서명해서 준다. 저장된 값을 고쳐도 서명이 깨져 입장이 거부되므로, 이 값만으로
// 다른 사람을 사칭하거나 차단을 우회할 수 없다. 로그인 상태면 계정도 서버가 확인해 토큰에 담는다.
export async function ensureBroadcastSession() {
  const stored = readStoredGuestToken();
  const result = await request('/v1/broadcast/session', {
    method: 'POST',
    body: { token: stored },
    authorize: true,
  });
  const token = String(result.token || '');
  if (token) storeGuestToken(token);
  return { token, displayName: String(result.displayName || '') };
}

export async function loadBroadcastState() {
  const result = await request('/v1/broadcast/state');
  return result.state || null;
}

export function broadcastSocketUrl(token) {
  return `${REALTIME_API_URL.replace(/^http/, 'ws')}/v1/broadcast/ws?token=${encodeURIComponent(token)}`;
}

// ------------------------------------------------- 중계 제어 (관리자 · 중계방 매니저)
//
// start · update · stop 은 중계방 매니저도 호출할 수 있다. entry 와 manager.* 는 플랫폼 관리자만이다.

function adminRequest(action, body) {
  return request(`/v1/broadcast/admin/${action}`, { method: 'POST', body, authorize: true });
}

export function startBroadcast({ url, title }) {
  return adminRequest('start', { url, title });
}

// 방송 세션은 그대로 두고 영상·제목만 고친다.
export function updateBroadcast({ url, title }) {
  return adminRequest('update', { url, title });
}

export function stopBroadcast() {
  return adminRequest('stop', {});
}

// 지난 글을 전부 치운다. 되돌릴 수 없다.
export function clearBroadcastChat() {
  return adminRequest('chat.clear', {});
}

// 중계방 입장 버튼을 내리거나 올린다. 닫으면 매니저가 아닌 접속자는 끊긴다.
export function setBroadcastEntryEnabled(enabled) {
  return adminRequest('entry', { enabled: Boolean(enabled) });
}

export function loadBroadcastManagers() {
  return adminRequest('managers', {});
}

export function setBroadcastManager({ userId, displayName, dcNickname, arcaNickname }) {
  return adminRequest('manager.set', { userId, displayName, dcNickname, arcaNickname });
}

export function removeBroadcastManager(userId) {
  return adminRequest('manager.remove', { userId });
}

export function publishBroadcastPlayback(sample, claim = false) {
  return request(`/v1/broadcast/admin/${claim ? 'playback.claim' : 'playback.sample'}`, {
    method: 'POST', body: sample, authorize: true, signal: AbortSignal.timeout(10_000),
  });
}

// ------------------------------------------------- 재생 목록 (관리자 · 중계방 매니저)

// 주소 여러 개를 줄바꿈으로 붙여 넣으면 한 번에 더한다. 영상 ID 와 제목은 워커가 뽑는다.
export function addToBroadcastPlaylist(urls) {
  return adminRequest('playlist.add', { urls: String(urls || '') });
}

export function removeFromBroadcastPlaylist(itemId) {
  return adminRequest('playlist.remove', { itemId });
}

// 화면에 보이는 순서 전체를 보낸다. 그 사이 목록이 바뀌었으면 서버가 거절한다.
export function moveBroadcastPlaylist(order) {
  return adminRequest('playlist.move', { order });
}

export function playBroadcastPlaylistItem(itemId) {
  return adminRequest('playlist.play', { itemId });
}

// 끝난(또는 넘기려는) 영상의 세대를 같이 보낸다. 서버는 그게 지금 영상일 때만 넘긴다 -
// 진행자 여럿이 동시에 '끝남' 을 알려도 한 번만 넘어간다.
export function playNextBroadcastPlaylist(fromKey) {
  return adminRequest('playlist.next', { fromKey: String(fromKey || '') });
}

export function clearBroadcastPlaylist() {
  return adminRequest('playlist.clear', {});
}

// 공유를 멈출 때 서버의 기준도 비운다. 안 비우면 시청자들이 최대 45초간 계속 따라간다.
export function releaseBroadcastPlayback(ownerToken) {
  return request('/v1/broadcast/admin/playback.release', {
    method: 'POST', body: { ownerToken }, authorize: true, signal: AbortSignal.timeout(10_000),
  });
}
